import { useState, useEffect, useMemo } from 'react';
import { SkeletonCard } from './Skeleton';
import {
    Globe,
    PanelLeft,
    Users,
    Plus,
    UserPlus,
    Clock,
    UserCheck,
    Compass,
} from 'lucide-react';
import { apiClient } from '../apiClient';
import Grid from './ui/Grid';
import Button from './ui/Button';
import SearchBar from './ui/SearchBar';
import PersonCard from './networking/PersonCard';
import GroupCard from './networking/GroupCard';
import CreateGroupModal from './networking/CreateGroupModal';
import { useToastContext } from './ToastProvider';
import './NetworkingView.css';

type ConnectionStatus = 'connected' | 'pending_sent' | 'pending_received' | 'none';

type Professional = {
    id: string;
    name: string;
    email: string;
    role: string;
    company: string;
    location: string;
    avatar: string;
    online: boolean;
    skills: string[];
    connectionStatus: ConnectionStatus;
};

type GroupChat = {
    id: string;
    name: string;
    members: Professional[];
    lastMessage?: string;
    lastMessageTime?: string;
    unreadCount?: number;
};

type NetworkingViewProps = {
    onMenuClick: () => void;
    showMenu: boolean;
    onMessageUser?: (professional: Professional) => void;
    onOpenGroupChat?: (group: GroupChat) => void;
    onCreateGroup?: (name: string, members: Professional[]) => void;
    initialSubTab?: string;
};

export type { Professional, GroupChat };

type SubView = 'connections' | 'requests' | 'discover' | 'groups';

function NetworkingView({
    onMenuClick,
    showMenu,
    onMessageUser,
    onOpenGroupChat,
    onCreateGroup,
    initialSubTab = 'connections',
}: NetworkingViewProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState<'all' | 'online' | 'nearby'>('all');
    const [activeView, setActiveView] = useState<SubView>(initialSubTab as SubView);
    const [showCreateGroup, setShowCreateGroup] = useState(false);

    const [allProfessionals, setAllProfessionals] = useState<Professional[]>([]);
    const [groupChats, setGroupChats] = useState<GroupChat[]>([]);
    const [loadingProfessionals, setLoadingProfessionals] = useState(true);
    const [loadingGroups, setLoadingGroups] = useState(true);
    const { error: toastError } = useToastContext();

    // Keep active sub-tab in sync with parent navigation
    useEffect(() => {
        setActiveView(initialSubTab as SubView);
    }, [initialSubTab]);

    // Reset search query when switching sub-tabs
    useEffect(() => {
        setSearchQuery('');
    }, [activeView]);

    // Fetch professionals
    useEffect(() => {
        let cancelled = false;
        async function fetchProfessionals() {
            try {
                const response = await apiClient.get('/networking/professionals/');
                const data = Array.isArray(response.data)
                    ? response.data
                    : response.data.results ?? [];
                const mapped: Professional[] = data.map((p: Record<string, unknown>) => ({
                    id: String(p.id ?? ''),
                    name: String(p.name ?? ''),
                    email: String(p.email ?? ''),
                    role: String(p.role ?? ''),
                    company: String(p.company ?? ''),
                    location: String(p.location ?? ''),
                    avatar: String(p.avatar ?? ''),
                    online: Boolean(p.online),
                    skills: Array.isArray(p.skills) ? p.skills.map(String) : [],
                    connectionStatus: (p.connection_status ?? p.connectionStatus ?? 'none') as ConnectionStatus,
                }));
                if (!cancelled) setAllProfessionals(mapped);
            } catch (error) {
                console.error('Failed to load professionals:', error);
                if (!cancelled) {
                    setAllProfessionals([]);
                    toastError("Couldn't load people. Please try again.");
                }
            } finally {
                if (!cancelled) setLoadingProfessionals(false);
            }
        }
        fetchProfessionals();
        return () => {
            cancelled = true;
        };
    }, [toastError]);

    // Fetch groups
    useEffect(() => {
        let cancelled = false;
        async function fetchGroups() {
            try {
                const response = await apiClient.get('/networking/groups/');
                const data = Array.isArray(response.data)
                    ? response.data
                    : response.data.results ?? [];
                const mapped: GroupChat[] = data.map((g: Record<string, unknown>) => ({
                    id: String(g.id ?? ''),
                    name: String(g.name ?? ''),
                    members: Array.isArray(g.members)
                        ? g.members.map((m: Record<string, unknown>) => ({
                              id: String(m.id ?? ''),
                              name: String(m.name ?? ''),
                              email: String(m.email ?? ''),
                              role: String(m.role ?? ''),
                              company: String(m.company ?? ''),
                              location: String(m.location ?? ''),
                              avatar: String(m.avatar ?? ''),
                              online: Boolean(m.online),
                              skills: Array.isArray(m.skills) ? m.skills.map(String) : [],
                              connectionStatus: (m.connection_status ??
                                  m.connectionStatus ??
                                  'none') as ConnectionStatus,
                          }))
                        : [],
                    lastMessage: String(g.last_message ?? g.lastMessage ?? ''),
                    lastMessageTime: String(g.last_message_time ?? g.lastMessageTime ?? ''),
                    unreadCount: Number(g.unread_count ?? g.unreadCount ?? 0),
                }));
                if (!cancelled) setGroupChats(mapped);
            } catch (error) {
                console.error('Failed to load group chats:', error);
                if (!cancelled) {
                    setGroupChats([]);
                    toastError("Couldn't load groups. Please try again.");
                }
            } finally {
                if (!cancelled) setLoadingGroups(false);
            }
        }
        fetchGroups();
        return () => {
            cancelled = true;
        };
    }, [toastError]);

    // Bucketed lists
    const connectedProfessionals = useMemo(
        () => allProfessionals.filter((p) => p.connectionStatus === 'connected'),
        [allProfessionals],
    );
    const pendingReceivedRequests = useMemo(
        () => allProfessionals.filter((p) => p.connectionStatus === 'pending_received'),
        [allProfessionals],
    );
    const pendingSentRequests = useMemo(
        () => allProfessionals.filter((p) => p.connectionStatus === 'pending_sent'),
        [allProfessionals],
    );
    const discoverProfessionals = useMemo(
        () => allProfessionals.filter((p) => p.connectionStatus === 'none'),
        [allProfessionals],
    );

    // Connection actions — same optimistic-state behavior as before
    const setStatus = (id: string, status: ConnectionStatus) => {
        setAllProfessionals((prev) =>
            prev.map((p) => (p.id === id ? { ...p, connectionStatus: status } : p)),
        );
    };
    const handleSendRequest = (id: string) => setStatus(id, 'pending_sent');
    const handleAcceptRequest = (id: string) => setStatus(id, 'connected');
    const handleDeclineRequest = (id: string) => setStatus(id, 'none');
    const handleCancelRequest = (id: string) => setStatus(id, 'none');

    // Shared search predicate
    const matches = (p: Professional, q: string) => {
        if (!q) return true;
        const needle = q.toLowerCase();
        return (
            p.name.toLowerCase().includes(needle) ||
            p.role.toLowerCase().includes(needle) ||
            p.company.toLowerCase().includes(needle) ||
            p.skills.some((s) => s.toLowerCase().includes(needle))
        );
    };

    const filteredConnections = useMemo(() => {
        return connectedProfessionals.filter((p) => {
            if (!matches(p, searchQuery)) return false;
            if (activeFilter === 'online') return p.online;
            if (activeFilter === 'nearby') return p.location.includes('Auckland');
            return true;
        });
    }, [connectedProfessionals, searchQuery, activeFilter]);

    const filteredDiscover = useMemo(
        () => discoverProfessionals.filter((p) => matches(p, searchQuery)),
        [discoverProfessionals, searchQuery],
    );

    const filteredReceived = useMemo(
        () => pendingReceivedRequests.filter((p) => matches(p, searchQuery)),
        [pendingReceivedRequests, searchQuery],
    );
    const filteredSent = useMemo(
        () => pendingSentRequests.filter((p) => matches(p, searchQuery)),
        [pendingSentRequests, searchQuery],
    );

    const filteredGroups = useMemo(() => {
        if (!searchQuery) return groupChats;
        const needle = searchQuery.toLowerCase();
        return groupChats.filter(
            (g) =>
                g.name.toLowerCase().includes(needle) ||
                (g.lastMessage ?? '').toLowerCase().includes(needle),
        );
    }, [groupChats, searchQuery]);

    const totalPendingCount = pendingReceivedRequests.length + pendingSentRequests.length;
    const totalGroupUnread = groupChats.reduce((sum, g) => sum + (g.unreadCount || 0), 0);

    const handleCreateGroup = (name: string, members: Professional[]) => {
        onCreateGroup?.(name, members);
        setShowCreateGroup(false);
    };

    const connectionsFilters = (
        <>
            <button
                type="button"
                className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
            >
                All
            </button>
            <button
                type="button"
                className={`filter-tab ${activeFilter === 'online' ? 'active' : ''}`}
                onClick={() => setActiveFilter('online')}
            >
                <span className="online-dot" /> Online
            </button>
            <button
                type="button"
                className={`filter-tab ${activeFilter === 'nearby' ? 'active' : ''}`}
                onClick={() => setActiveFilter('nearby')}
            >
                Nearby
            </button>
        </>
    );

    const searchPlaceholder: Record<SubView, string> = {
        connections: 'Search by name, role, or skill…',
        requests: 'Search pending requests…',
        discover: 'Search professionals to connect with…',
        groups: 'Search groups…',
    };

    return (
        <div className="networking-view">
            {/* Header */}
            <header
                className="networking-header"
                onClick={() => {
                    const surface = document.querySelector('.networking-content');
                    surface?.scrollTo({ top: 0, behavior: 'smooth' });
                }}
            >
                {showMenu && (
                    <button
                        type="button"
                        className="menu-btn"
                        onClick={onMenuClick}
                        aria-label="Open menu"
                    >
                        <PanelLeft size={20} />
                    </button>
                )}
                <div className="header-content">
                    <span className="header-icon">
                        <Globe size={24} />
                    </span>
                    <h1 className="header-title">Networking</h1>
                </div>
                <div className="header-actions">
                    <div className="header-stats">
                        <span className="stat">
                            <span className="stat-value">
                                {connectedProfessionals.filter((p) => p.online).length}
                            </span>
                            <span className="stat-label">Online</span>
                        </span>
                        <span className="stat">
                            <span className="stat-value">{connectedProfessionals.length}</span>
                            <span className="stat-label">Connections</span>
                        </span>
                    </div>
                </div>
            </header>

            {/* Sub-tab toggle (mobile only — desktop has it in the sidebar) */}
            <div className="view-toggle-section mobile-only">
                <div className="view-toggle expanded">
                    <button
                        type="button"
                        className={`view-toggle-btn ${activeView === 'connections' ? 'active' : ''}`}
                        onClick={() => setActiveView('connections')}
                    >
                        <UserCheck size={18} />
                        <span>Connections</span>
                    </button>
                    <button
                        type="button"
                        className={`view-toggle-btn ${activeView === 'requests' ? 'active' : ''}`}
                        onClick={() => setActiveView('requests')}
                    >
                        <Clock size={18} />
                        <span>Requests</span>
                        {totalPendingCount > 0 && (
                            <span className="unread-badge">{totalPendingCount}</span>
                        )}
                    </button>
                    <button
                        type="button"
                        className={`view-toggle-btn ${activeView === 'discover' ? 'active' : ''}`}
                        onClick={() => setActiveView('discover')}
                    >
                        <Compass size={18} />
                        <span>Discover</span>
                    </button>
                    <button
                        type="button"
                        className={`view-toggle-btn ${activeView === 'groups' ? 'active' : ''}`}
                        onClick={() => setActiveView('groups')}
                    >
                        <Users size={18} />
                        <span>Groups</span>
                        {totalGroupUnread > 0 && (
                            <span className="unread-badge">{totalGroupUnread}</span>
                        )}
                    </button>
                </div>
            </div>

            {/* Shared search bar for all sub-tabs */}
            <div className="networking-search-section">
                <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={searchPlaceholder[activeView]}
                    filters={activeView === 'connections' ? connectionsFilters : undefined}
                />
            </div>

            {/* Content area — single scroll surface so all tabs share padding + empty-state rules */}
            <div className="networking-content">
                {activeView === 'connections' && (
                    <Grid minItemWidth={300} gap="lg">
                        {loadingProfessionals ? (
                            Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
                        ) : filteredConnections.length === 0 ? (
                            <div className="networking-empty">
                                <div className="empty-state">
                                    <div className="empty-icon">
                                        <UserCheck size={48} />
                                    </div>
                                    <h2>No connections found</h2>
                                    <p>
                                        Try adjusting your search or discover new professionals to
                                        connect with
                                    </p>
                                    <Button
                                        variant="primary"
                                        size="md"
                                        icon={<Compass size={18} />}
                                        onClick={() => setActiveView('discover')}
                                    >
                                        Discover people
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            filteredConnections.map((professional) => (
                                <PersonCard
                                    key={professional.id}
                                    professional={professional}
                                    variant="connection"
                                    onMessage={onMessageUser}
                                />
                            ))
                        )}
                    </Grid>
                )}

                {activeView === 'requests' && (
                    <>
                        {loadingProfessionals ? (
                            <Grid minItemWidth={360} gap="md">
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <SkeletonCard key={i} />
                                ))}
                            </Grid>
                        ) : filteredReceived.length === 0 && filteredSent.length === 0 ? (
                            <div className="networking-empty">
                                <div className="empty-state">
                                    <div className="empty-icon">
                                        <Clock size={48} />
                                    </div>
                                    <h2>
                                        {searchQuery
                                            ? 'No matching requests'
                                            : 'No pending requests'}
                                    </h2>
                                    <p>
                                        {searchQuery
                                            ? 'Try a different search term.'
                                            : "When you send or receive connection requests, they'll appear here"}
                                    </p>
                                    {!searchQuery && (
                                        <Button
                                            variant="primary"
                                            size="md"
                                            icon={<Compass size={18} />}
                                            onClick={() => setActiveView('discover')}
                                        >
                                            Discover people
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                {filteredReceived.length > 0 && (
                                    <section className="requests-section">
                                        <h2 className="requests-section-title">
                                            <UserPlus size={20} />
                                            Received requests ({filteredReceived.length})
                                        </h2>
                                        <Grid minItemWidth={360} gap="md">
                                            {filteredReceived.map((professional) => (
                                                <PersonCard
                                                    key={professional.id}
                                                    professional={professional}
                                                    variant="request-received"
                                                    onAccept={handleAcceptRequest}
                                                    onDecline={handleDeclineRequest}
                                                />
                                            ))}
                                        </Grid>
                                    </section>
                                )}
                                {filteredSent.length > 0 && (
                                    <section className="requests-section">
                                        <h2 className="requests-section-title">
                                            <Clock size={20} />
                                            Sent requests ({filteredSent.length})
                                        </h2>
                                        <Grid minItemWidth={360} gap="md">
                                            {filteredSent.map((professional) => (
                                                <PersonCard
                                                    key={professional.id}
                                                    professional={professional}
                                                    variant="request-sent"
                                                    onCancel={handleCancelRequest}
                                                />
                                            ))}
                                        </Grid>
                                    </section>
                                )}
                            </>
                        )}
                    </>
                )}

                {activeView === 'discover' && (
                    <Grid minItemWidth={300} gap="lg">
                        {loadingProfessionals ? (
                            Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
                        ) : filteredDiscover.length === 0 ? (
                            <div className="networking-empty">
                                <div className="empty-state">
                                    <div className="empty-icon">
                                        <Compass size={48} />
                                    </div>
                                    <h2>No professionals to discover</h2>
                                    <p>
                                        You've connected with everyone! Check back later for new
                                        professionals.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            filteredDiscover.map((professional) => (
                                <PersonCard
                                    key={professional.id}
                                    professional={professional}
                                    variant="discover"
                                    onConnect={handleSendRequest}
                                />
                            ))
                        )}
                    </Grid>
                )}

                {activeView === 'groups' && (
                    <>
                        <div className="groups-toolbar">
                            <Button
                                variant="secondary"
                                size="md"
                                icon={<Plus size={16} />}
                                onClick={() => setShowCreateGroup(true)}
                            >
                                New group
                            </Button>
                        </div>
                        {loadingGroups ? (
                            <Grid minItemWidth={320} gap="md">
                                {Array.from({ length: 4 }).map((_, i) => (
                                    <SkeletonCard key={i} />
                                ))}
                            </Grid>
                        ) : filteredGroups.length === 0 ? (
                            <div className="networking-empty">
                                <div className="empty-state">
                                    <div className="empty-icon">
                                        <Users size={48} />
                                    </div>
                                    <h2>
                                        {searchQuery
                                            ? 'No matching groups'
                                            : 'No group chats yet'}
                                    </h2>
                                    <p>
                                        {searchQuery
                                            ? 'Try a different search term.'
                                            : 'Create a group with your connections to start chatting'}
                                    </p>
                                    {!searchQuery && (
                                        <Button
                                            variant="primary"
                                            size="md"
                                            icon={<Plus size={16} />}
                                            onClick={() => setShowCreateGroup(true)}
                                        >
                                            Create group
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <Grid minItemWidth={320} gap="md">
                                {filteredGroups.map((group) => (
                                    <GroupCard
                                        key={group.id}
                                        group={group}
                                        onOpen={(g) => onOpenGroupChat?.(g)}
                                    />
                                ))}
                            </Grid>
                        )}
                    </>
                )}
            </div>

            <CreateGroupModal
                open={showCreateGroup}
                onClose={() => setShowCreateGroup(false)}
                connections={connectedProfessionals}
                onCreate={handleCreateGroup}
            />
        </div>
    );
}

export default NetworkingView;
